import { useLayoutEffect, useMemo, useState } from 'react';
import type { XYPosition } from '@xyflow/react';
import { MOVE_EASE } from './motion';

type Positions = ReadonlyMap<string, XYPosition>;

/**
 * The nodes of `to`, each `k` of the way from its place in `from` (0 = there, 1 = at `to`). A node `from` does not
 * place starts at its place in `to`; a node only `from` places is gone. `to` itself once `k` reaches 1.
 */
export function interpolate(from: Positions, to: Positions, k: number): Positions {
  if (k >= 1) return to;
  return new Map(
    [...to].map(([id, end]) => {
      const start = from.get(id);
      return [id, start ? { x: start.x + (end.x - start.x) * k, y: start.y + (end.y - start.y) * k } : end];
    }),
  );
}

/** Moves the drawn positions to each new target, one animation frame at a time. */
export type PositionMover = {
  /** The positions drawn last. */
  onScreen: () => Positions;
  /** The positions drawn when the latest target came: where each node of the layout before it was on screen. */
  before: () => Positions;
  /**
   * Starts the move to `target` from the positions on screen, over `durationMs` eased by `MOVE_EASE`, calling
   * `draw` with the start at once and then once per animation frame. The clock starts at the first animation frame
   * (t = 0), not at the call, so a slow render of the new layout does not use up the move. A duration of 0 draws
   * `target` at once. Returns the cancel, which stops the frames.
   */
  moveTo: (target: Positions, durationMs: number, draw: (positions: Positions) => void) => () => void;
};

export function createPositionMover(initial: Positions): PositionMover {
  const state = { onScreen: initial, before: initial, target: initial };
  const moveTo = (target: Positions, durationMs: number, draw: (positions: Positions) => void) => {
    // A second start for the same target (React runs a layout effect twice in development) keeps `before`.
    if (target !== state.target) {
      state.before = state.onScreen;
      state.target = target;
    }
    const from = state.onScreen;
    const show = (next: Positions) => {
      state.onScreen = next;
      draw(next);
    };
    if (durationMs <= 0 || from === target) {
      show(target);
      return () => {};
    }
    const frame = { id: 0, start: NaN };
    const step = (now: number) => {
      // The first frame is t = 0 and draws nothing new: the start is already on screen.
      if (Number.isNaN(frame.start)) frame.start = now;
      const t = Math.min(1, Math.max(0, now - frame.start) / durationMs);
      if (t > 0) show(interpolate(from, target, MOVE_EASE(t)));
      if (t < 1) frame.id = requestAnimationFrame(step);
    };
    show(interpolate(from, target, 0));
    frame.id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.id);
  };
  return { onScreen: () => state.onScreen, before: () => state.before, moveTo };
}

/**
 * The positions to draw while the nodes move to `target`: over `durationMs`, eased by `MOVE_EASE`, each node goes
 * from where it is on screen to its new place, one animation frame at a time. Positions drive the cards and the
 * edges alike, so the edges stay on the cards. A new target cancels a running move and starts from where the
 * nodes are. A duration of 0 (reduced motion, or too many nodes) or a first layout lands at once. The mover also
 * says where the nodes were drawn when the target came (see `PositionMover.before`).
 */
export function useAnimatedPositions(
  target: Positions,
  durationMs: number,
): { positions: Positions; mover: PositionMover } {
  const [mover] = useState(() => createPositionMover(target));
  const [shown, setShown] = useState(target);
  useLayoutEffect(() => mover.moveTo(target, durationMs, setShown), [mover, target, durationMs]);
  // Before the move starts, a new target draws each node where it was and a new node in place, so nothing jumps.
  // A frame of the move already holds exactly the target's nodes.
  const positions = useMemo(
    () =>
      shown === target || (shown.size === target.size && [...target.keys()].every((id) => shown.has(id)))
        ? shown
        : interpolate(shown, target, 0),
    [shown, target],
  );
  return { positions, mover };
}
